import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MapPin,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { attendanceService } from '@/services/attendance.service';
import {
  GeolocationError,
  getCurrentLocation,
  queryGeolocationPermission,
  type GeoErrorReason,
  type PermissionState,
} from '@/utils/geolocation';
import { cn } from '@/utils/cn';

export type PunchMode = 'in' | 'out';

interface CapturedLocation {
  lat: number;
  lng: number;
  accuracy?: number;
}

function GeoErrorBox({
  error,
  onRetry,
}: {
  error: { reason: GeoErrorReason; message: string };
  onRetry: () => void;
}) {
  const help =
    error.reason === 'permission-denied'
      ? 'Click the lock icon in the address bar → Site settings → set Location to "Allow", then try again.'
      : error.reason === 'insecure-context'
        ? 'Open the app over HTTPS or via http://localhost. Geolocation is blocked on plain HTTP origins.'
        : error.reason === 'unavailable'
          ? 'Make sure system Location Services are enabled and you have a working GPS / Wi-Fi signal.'
          : error.reason === 'timeout'
            ? 'Your device took too long to respond. Move to a clearer area and retry.'
            : error.reason === 'unsupported'
              ? 'Use a modern browser (Chrome, Edge, Firefox, Safari).'
              : null;

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
      <div className="flex items-start gap-2 text-rose-700">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="flex-1">
          <p className="font-medium">{error.message}</p>
          {help && <p className="mt-1 text-rose-700/80">{help}</p>}
        </div>
      </div>
      <div className="mt-2 flex justify-end">
        <button
          onClick={onRetry}
          className="rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export function PunchModal({
  open,
  mode,
  onClose,
  onDone,
}: {
  open: boolean;
  mode: PunchMode;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [locating, setLocating] = useState(false);
  const [location, setLocation] = useState<CapturedLocation | null>(null);
  const [permState, setPermState] = useState<PermissionState | null>(null);
  const [geoError, setGeoError] = useState<{
    reason: GeoErrorReason;
    message: string;
  } | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const fetchLocation = async () => {
    setGeoError(null);
    setLocating(true);
    try {
      const loc = await getCurrentLocation();
      setLocation(loc);
      setPermState('granted');
    } catch (e: any) {
      setLocation(null);
      if (e instanceof GeolocationError) {
        setGeoError({ reason: e.reason, message: e.message });
      } else {
        setGeoError({
          reason: 'unknown',
          message: e?.message ?? 'Could not get your location',
        });
      }
      queryGeolocationPermission().then(setPermState);
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    if (open) {
      setNote('');
      setLocation(null);
      setGeoError(null);
      setApiError(null);
      setPermState(null);
      queryGeolocationPermission().then(setPermState);
      fetchLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (loc: CapturedLocation) =>
      mode === 'in'
        ? attendanceService.checkIn({ location: loc, note: note || undefined })
        : attendanceService.checkOut({ location: loc, note: note || undefined }),
    onSuccess: () => {
      toast.success(mode === 'in' ? 'Checked in' : 'Checked out');
      onDone();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        'Failed to mark attendance';
      setApiError(msg);
    },
  });

  const submit = () => {
    if (!location) return;
    setApiError(null);
    mutation.mutate(location);
  };

  const isIn = mode === 'in';
  const canSubmit = !!location && !mutation.isPending && !locating;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={isIn ? 'Check in' : 'Check out'}
      description={
        isIn
          ? 'Confirm your location to start the day.'
          : 'Confirm your location to end the day.'
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending || locating}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={mutation.isPending}
            disabled={!canSubmit}
          >
            <MapPin className="h-4 w-4" />
            {isIn ? 'Check in' : 'Check out'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {permState && (
          <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-1.5 text-[11px] text-muted-foreground">
            <span>Browser permission:</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold',
                permState === 'granted'
                  ? 'bg-emerald-100 text-emerald-700'
                  : permState === 'denied'
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-amber-100 text-amber-700',
              )}
            >
              {permState}
            </span>
          </div>
        )}

        {locating && (
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/30 px-3 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
            <span>Getting your location…</span>
          </div>
        )}

        {location && !locating && !geoError && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-emerald-900">
                  Location captured
                </p>
                <p className="mt-0.5 break-all font-mono text-xs text-emerald-800">
                  {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
                {location.accuracy && (
                  <p className="mt-0.5 text-[11px] text-emerald-700/80">
                    Accuracy ± {Math.round(location.accuracy)} m
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={fetchLocation}
                className="rounded-md p-1 text-emerald-700 hover:bg-emerald-100"
                aria-label="Refresh location"
                title="Refresh location"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {geoError && !locating && (
          <GeoErrorBox error={geoError} onRetry={fetchLocation} />
        )}

        <div className="space-y-1.5">
          <label
            className="text-sm font-medium text-foreground"
            htmlFor="punch-note"
          >
            Note (optional)
          </label>
          <textarea
            id="punch-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={isIn ? 'Working from office today…' : 'Wrapping up…'}
            rows={2}
            className="w-full resize-none rounded-xl border border-input bg-white/80 px-3 py-2 text-sm shadow-card outline-none placeholder:text-muted-foreground focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100"
          />
        </div>

        {apiError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
            <div className="flex items-start gap-2 text-rose-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p className="flex-1 font-medium">{apiError}</p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
